import React, { useState, useMemo } from 'react';
import {
  Calculator,
  Home,
  FileSpreadsheet,
  TrendingDown,
  Percent,
  ShieldCheck,
  RefreshCw,
  Sliders,
  Scale,
  Activity,
  CheckCircle2,
  AlertTriangle,
  Info,
  Calendar,
  DollarSign,
  ChevronDown,
  ChevronRight,
  Download,
  Bookmark,
  BookmarkCheck,
  Building,
  ArrowRight,
  Sparkles,
  ExternalLink,
  PieChart,
  HelpCircle,
} from 'lucide-react';
import {
  LoanCalculationInput,
  LoanCalculationMode,
  LoanProductType,
  LoanPaymentFrequency,
  CompoundingConvention,
  LoanRateType,
  SavedLoanScenario,
  LoanCalculationResult,
} from '../../types';
import {
  LENDING_COUNTRY_PACKS,
  LOAN_TELEMETRY_DATA,
  RECENT_LENDING_INCIDENTS,
} from '../../data/loanCountryPacksData';
import {
  calculateLoan,
  calculateAffordability,
  calculateRefinance,
  runGoldenLoanTests,
  getPeriodsPerYear,
} from '../../services/loanEngine';

export const LoanIntelligenceHub: React.FC = () => {
  // Active sub-mode
  const [activeMode, setActiveMode] = useState<LoanCalculationMode>('mortgage-calculator');

  // Core Form State
  const [countryCode, setCountryCode] = useState<string>('US');
  const pack = LENDING_COUNTRY_PACKS[countryCode] || LENDING_COUNTRY_PACKS['US'];

  const [productType, setProductType] = useState<LoanProductType>('MORTGAGE');
  const [purchasePrice, setPurchasePrice] = useState<number>(450000);
  const [downPayment, setDownPayment] = useState<number>(90000);
  const [downPaymentPercent, setDownPaymentPercent] = useState<number>(20);
  const [loanAmount, setLoanAmount] = useState<number>(360000);

  const [interestRate, setInterestRate] = useState<number>(6.5);
  const [rateType, setRateType] = useState<LoanRateType>('FIXED');
  const [termYears, setTermYears] = useState<number>(30);
  const [amortizationYears, setAmortizationYears] = useState<number>(30);
  const [paymentFrequency, setPaymentFrequency] = useState<LoanPaymentFrequency>('monthly');
  const [compounding, setCompounding] = useState<CompoundingConvention>('MONTHLY');

  // Escrow & Fees
  const [propertyTaxAnnual, setPropertyTaxAnnual] = useState<number>(4950);
  const [homeInsuranceAnnual, setHomeInsuranceAnnual] = useState<number>(1890);
  const [hoaMonthly, setHoaMonthly] = useState<number>(0);
  const [upfrontClosingCosts, setUpfrontClosingCosts] = useState<number>(3500);

  // Extra payments
  const [extraPaymentMonthly, setExtraPaymentMonthly] = useState<number>(0);
  const [extraPaymentAnnual, setExtraPaymentAnnual] = useState<number>(0);
  const [oneTimeExtraAmount, setOneTimeExtraAmount] = useState<number>(0);
  const [oneTimeExtraMonth, setOneTimeExtraMonth] = useState<number>(12);

  // ARM scenario
  const [armRateIncrease, setArmRateIncrease] = useState<number>(1.5);

  // Affordability state
  const [grossAnnualIncome, setGrossAnnualIncome] = useState<number>(120000);
  const [monthlyDebtPayments, setMonthlyDebtPayments] = useState<number>(600);
  const [downPaymentSaved, setDownPaymentSaved] = useState<number>(80000);

  // Refinance state
  const [refinanceCurrentBalance, setRefinanceCurrentBalance] = useState<number>(320000);
  const [refinanceCurrentRate, setRefinanceCurrentRate] = useState<number>(7.25);
  const [refinanceCurrentRemainingYears, setRefinanceCurrentRemainingYears] = useState<number>(27);
  const [refinanceNewRate, setRefinanceNewRate] = useState<number>(5.85);
  const [refinanceNewTermYears, setRefinanceNewTermYears] = useState<number>(30);
  const [refinanceClosingCostsInput, setRefinanceClosingCostsInput] = useState<number>(4500);

  // UI Table & UI state
  const [amortizationViewType, setAmortizationViewType] = useState<'yearly' | 'monthly'>('yearly');
  const [schedulePage, setSchedulePage] = useState<number>(1);
  const [showExplainability, setShowExplainability] = useState<boolean>(false);
  const [savedScenarios, setSavedScenarios] = useState<SavedLoanScenario[]>([]);
  const [isScenarioSaved, setIsScenarioSaved] = useState<boolean>(false);
  const [testSuiteRunResult, setTestSuiteRunResult] = useState<ReturnType<typeof runGoldenLoanTests> | null>(null);

  // Handle Country Change
  const handleCountryChange = (newCountry: string) => {
    setCountryCode(newCountry);
    const newPack = LENDING_COUNTRY_PACKS[newCountry] || LENDING_COUNTRY_PACKS['US'];
    setCompounding(newPack.standardCompounding);
    setTermYears(newPack.defaultMortgageTermYears);
    setAmortizationYears(newPack.standardMortgageAmortizationYears);
    if (!newPack.supportedFrequencies.includes(paymentFrequency)) {
      setPaymentFrequency(newPack.supportedFrequencies[0] || 'monthly');
    }
  };

  // Bidirectional Down Payment Sync
  const handlePriceChange = (newPrice: number) => {
    setPurchasePrice(newPrice);
    const newDown = (newPrice * downPaymentPercent) / 100;
    setDownPayment(newDown);
    setLoanAmount(newPrice - newDown);
    setPropertyTaxAnnual(Math.round(newPrice * pack.defaultPropertyTaxRate));
    setHomeInsuranceAnnual(Math.round(newPrice * pack.defaultHomeInsuranceRate));
  };

  const handleDownPaymentAmountChange = (newDown: number) => {
    setDownPayment(newDown);
    if (purchasePrice > 0) {
      setDownPaymentPercent(Math.round((newDown / purchasePrice) * 1000) / 10);
      setLoanAmount(purchasePrice - newDown);
    }
  };

  const handleDownPaymentPercentChange = (newPercent: number) => {
    setDownPaymentPercent(newPercent);
    if (purchasePrice > 0) {
      const newDown = (purchasePrice * newPercent) / 100;
      setDownPayment(newDown);
      setLoanAmount(purchasePrice - newDown);
    }
  };

  // Main Calculation Execution
  const calculationResult: LoanCalculationResult = useMemo(() => {
    const input: LoanCalculationInput = {
      mode: activeMode,
      productType: activeMode === 'loan-calculator' ? 'PERSONAL_LOAN' : productType,
      countryCode,
      currency: pack.defaultCurrency,
      loanAmount: activeMode === 'mortgage-calculator' ? purchasePrice - downPayment : loanAmount,
      purchasePrice,
      downPayment,
      downPaymentPercent,
      interestRate,
      rateType,
      termYears,
      amortizationYears,
      paymentFrequency,
      compoundingConvention: compounding,
      propertyTaxAnnual,
      homeInsuranceAnnual,
      hoaMonthly,
      closingCostsUpfront: upfrontClosingCosts,
      extraPaymentMonthly,
      extraPaymentAnnual,
      oneTimeExtraAmount,
      oneTimeExtraPaymentNumber: oneTimeExtraMonth,
      armRateIncreaseScenario: armRateIncrease,
    };

    return calculateLoan(input);
  }, [
    activeMode,
    productType,
    countryCode,
    pack,
    purchasePrice,
    downPayment,
    downPaymentPercent,
    loanAmount,
    interestRate,
    rateType,
    termYears,
    amortizationYears,
    paymentFrequency,
    compounding,
    propertyTaxAnnual,
    homeInsuranceAnnual,
    hoaMonthly,
    upfrontClosingCosts,
    extraPaymentMonthly,
    extraPaymentAnnual,
    oneTimeExtraAmount,
    oneTimeExtraMonth,
    armRateIncrease,
  ]);

  // Affordability Calculation
  const affordabilityResult = useMemo(() => {
    return calculateAffordability({
      grossAnnualIncome,
      monthlyDebtPayments,
      downPaymentSaved,
      interestRate,
      termYears: amortizationYears,
      countryCode,
    });
  }, [grossAnnualIncome, monthlyDebtPayments, downPaymentSaved, interestRate, amortizationYears, countryCode]);

  // Refinance Calculation
  const refinanceResult = useMemo(() => {
    return calculateRefinance({
      currentBalance: refinanceCurrentBalance,
      currentRate: refinanceCurrentRate,
      currentRemainingYears: refinanceCurrentRemainingYears,
      currentMonthlyPayment: 0,
      newRate: refinanceNewRate,
      newTermYears: refinanceNewTermYears,
      refinanceClosingCosts: refinanceClosingCostsInput,
    });
  }, [
    refinanceCurrentBalance,
    refinanceCurrentRate,
    refinanceCurrentRemainingYears,
    refinanceNewRate,
    refinanceNewTermYears,
    refinanceClosingCostsInput,
  ]);

  // Save Scenario Handler
  const handleSaveScenario = () => {
    const newScenario: SavedLoanScenario = {
      id: `SCENARIO-${Date.now()}`,
      name: `${pack.countryName} ${termYears}y ${interestRate}% ($${(purchasePrice || loanAmount).toLocaleString()})`,
      createdAt: new Date().toLocaleDateString(),
      input: calculationResult.input,
      result: calculationResult,
    };
    setSavedScenarios((prev) => [newScenario, ...prev]);
    setIsScenarioSaved(true);
    setTimeout(() => setIsScenarioSaved(false), 3000);
  };

  const handleRunTestSuite = () => {
    const res = runGoldenLoanTests();
    setTestSuiteRunResult(res);
  };

  return (
    <div className="min-h-screen bg-[#FBFBFC] text-[#1A1A1A] pb-24">
      {/* Top Header Banner */}
      <div className="border-b border-[#E5E7EB] bg-white sticky top-0 z-30 shadow-2xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#0E7044] text-white flex items-center justify-center font-bold shadow-xs">
              <Home className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-lg font-extrabold text-[#1A1A1A] tracking-tight">
                  YamooPay Lending & Mortgage Engine
                </h1>
                <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-[#E8F5E9] text-[#0E7044] border border-[#C8E6C9]">
                  PROMPT 07
                </span>
              </div>
              <p className="text-xs text-[#6B7280]">
                Universal Amortization Math • Sovereign Compounding • PITI Housing Escrow • APRC/TAEG Solver
              </p>
            </div>
          </div>

          {/* Country Jurisdiction Selector */}
          <div className="flex items-center gap-2">
            <label className="text-xs font-semibold text-[#6B7280] hidden sm:inline">Market Pack:</label>
            <select
              value={countryCode}
              onChange={(e) => handleCountryChange(e.target.value)}
              className="px-3 py-1.5 bg-[#F9FAFB] border border-[#D1D5DB] rounded-lg text-xs font-bold text-[#1A1A1A] focus:outline-none focus:ring-2 focus:ring-[#0E7044] cursor-pointer"
            >
              {Object.values(LENDING_COUNTRY_PACKS).map((c) => (
                <option key={c.countryCode} value={c.countryCode}>
                  {c.countryName} ({c.defaultCurrency} • {c.aprMetricName.split(' ')[0]})
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Sub-Navigation Modes */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex overflow-x-auto gap-1 py-1.5 scrollbar-none border-t border-[#F3F4F6]">
          {[
            { id: 'mortgage-calculator', label: 'Mortgage & PITI', icon: Home },
            { id: 'loan-calculator', label: 'Loan Calculator', icon: Calculator },
            { id: 'amortization', label: 'Amortization Table', icon: FileSpreadsheet },
            { id: 'extra-payments', label: 'Extra Payments & Payoff', icon: TrendingDown },
            { id: 'apr-calculator', label: 'APR & Cost of Credit', icon: Percent },
            { id: 'affordability', label: 'Home Affordability', icon: Sliders },
            { id: 'refinance', label: 'Refinance Analyzer', icon: RefreshCw },
            { id: 'fixed-vs-variable', label: 'Fixed vs Variable Stress', icon: Activity },
            { id: 'scenario-comparison', label: 'Scenario Comparison', icon: Scale },
            { id: 'command-center', label: 'Command Center & QA', icon: ShieldCheck },
          ].map((tab) => {
            const Icon = tab.icon;
            const isActive = activeMode === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveMode(tab.id as LoanCalculationMode)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${
                  isActive
                    ? 'bg-[#0E7044] text-white shadow-2xs'
                    : 'text-[#4B5563] hover:bg-[#F3F4F6] hover:text-[#1A1A1A]'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 space-y-6">
        {/* ========================================================================= */}
        {/* 1. MORTGAGE & LOAN MAIN CALCULATOR VIEW */}
        {/* ========================================================================= */}
        {(activeMode === 'mortgage-calculator' ||
          activeMode === 'loan-calculator' ||
          activeMode === 'extra-payments' ||
          activeMode === 'apr-calculator' ||
          activeMode === 'fixed-vs-variable') && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left Inputs Panel */}
            <div className="lg:col-span-5 space-y-5">
              <div className="bg-white border border-[#E5E7EB] rounded-2xl p-5 sm:p-6 shadow-xs space-y-5">
                <div className="flex items-center justify-between border-b border-[#E5E7EB] pb-3">
                  <div className="flex items-center gap-2">
                    <Sliders className="w-4 h-4 text-[#0E7044]" />
                    <h2 className="font-bold text-sm text-[#1A1A1A]">
                      {activeMode === 'mortgage-calculator' ? 'Mortgage Parameters' : 'Loan Parameters'}
                    </h2>
                  </div>
                  <span className="text-[11px] font-mono font-bold text-[#0E7044] bg-[#E8F5E9] px-2 py-0.5 rounded">
                    {pack.rulesetVersion}
                  </span>
                </div>

                {/* Mortgage Specific: Home Price & Down Payment */}
                {activeMode === 'mortgage-calculator' ? (
                  <>
                    <div className="space-y-1.5">
                      <div className="flex justify-between items-center">
                        <label className="text-xs font-bold text-[#374151]">Home Purchase Price</label>
                        <span className="text-xs font-mono font-bold text-[#1A1A1A]">
                          {pack.defaultCurrency} {purchasePrice.toLocaleString()}
                        </span>
                      </div>
                      <div className="relative">
                        <span className="absolute left-3 top-2.5 text-xs text-[#9CA3AF] font-bold">
                          {pack.defaultCurrency}
                        </span>
                        <input
                          type="number"
                          value={purchasePrice}
                          onChange={(e) => handlePriceChange(Number(e.target.value))}
                          className="w-full pl-12 pr-3 py-2 border border-[#D1D5DB] rounded-xl text-sm font-bold text-[#1A1A1A] focus:ring-2 focus:ring-[#0E7044] focus:outline-none"
                        />
                      </div>
                      <input
                        type="range"
                        min={50000}
                        max={2000000}
                        step={10000}
                        value={purchasePrice}
                        onChange={(e) => handlePriceChange(Number(e.target.value))}
                        className="w-full accent-[#0E7044] cursor-pointer"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <label className="text-xs font-bold text-[#374151]">Down Payment ($)</label>
                        <input
                          type="number"
                          value={downPayment}
                          onChange={(e) => handleDownPaymentAmountChange(Number(e.target.value))}
                          className="w-full px-3 py-2 border border-[#D1D5DB] rounded-xl text-xs font-bold text-[#1A1A1A] focus:ring-2 focus:ring-[#0E7044] focus:outline-none"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-xs font-bold text-[#374151]">Down Payment (%)</label>
                        <div className="flex gap-1">
                          <input
                            type="number"
                            step="0.5"
                            value={downPaymentPercent}
                            onChange={(e) => handleDownPaymentPercentChange(Number(e.target.value))}
                            className="w-full px-3 py-2 border border-[#D1D5DB] rounded-xl text-xs font-bold text-[#1A1A1A] focus:ring-2 focus:ring-[#0E7044] focus:outline-none"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Quick Down Payment Percent Pills */}
                    <div className="flex gap-1.5">
                      {[5, 10, 15, 20, 25, 30].map((pct) => (
                        <button
                          key={pct}
                          onClick={() => handleDownPaymentPercentChange(pct)}
                          className={`flex-1 py-1 rounded-lg text-[11px] font-bold transition-all cursor-pointer ${
                            downPaymentPercent === pct
                              ? 'bg-[#0E7044] text-white'
                              : 'bg-[#F3F4F6] text-[#4B5563] hover:bg-[#E5E7EB]'
                          }`}
                        >
                          {pct}%
                        </button>
                      ))}
                    </div>
                  </>
                ) : (
                  /* Generic Loan Amount */
                  <div className="space-y-1.5">
                    <div className="flex justify-between items-center">
                      <label className="text-xs font-bold text-[#374151]">Loan Principal Amount</label>
                      <span className="text-xs font-mono font-bold text-[#1A1A1A]">
                        {pack.defaultCurrency} {loanAmount.toLocaleString()}
                      </span>
                    </div>
                    <div className="relative">
                      <span className="absolute left-3 top-2.5 text-xs text-[#9CA3AF] font-bold">
                        {pack.defaultCurrency}
                      </span>
                      <input
                        type="number"
                        value={loanAmount}
                        onChange={(e) => setLoanAmount(Number(e.target.value))}
                        className="w-full pl-12 pr-3 py-2 border border-[#D1D5DB] rounded-xl text-sm font-bold text-[#1A1A1A] focus:ring-2 focus:ring-[#0E7044] focus:outline-none"
                      />
                    </div>
                  </div>
                )}

                {/* Interest Rate & Rate Type */}
                <div className="space-y-1.5 pt-1">
                  <div className="flex justify-between items-center">
                    <label className="text-xs font-bold text-[#374151]">Annual Interest Rate (%)</label>
                    <span className="text-xs font-mono font-bold text-[#0E7044]">{interestRate}%</span>
                  </div>
                  <input
                    type="number"
                    step="0.05"
                    value={interestRate}
                    onChange={(e) => setInterestRate(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-[#D1D5DB] rounded-xl text-sm font-bold text-[#1A1A1A] focus:ring-2 focus:ring-[#0E7044] focus:outline-none"
                  />
                  <input
                    type="range"
                    min={1.0}
                    max={15.0}
                    step={0.1}
                    value={interestRate}
                    onChange={(e) => setInterestRate(Number(e.target.value))}
                    className="w-full accent-[#0E7044] cursor-pointer"
                  />
                </div>

                {/* Term & Amortization Period */}
                <div className="space-y-2 pt-1">
                  <label className="text-xs font-bold text-[#374151]">
                    {countryCode === 'CA' ? 'Amortization Period (Total Loan Life)' : 'Loan Term'}
                  </label>
                  <div className="grid grid-cols-4 gap-1.5">
                    {[10, 15, 20, 25, 30].map((yr) => (
                      <button
                        key={yr}
                        onClick={() => {
                          setTermYears(yr);
                          setAmortizationYears(yr);
                        }}
                        className={`py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                          amortizationYears === yr
                            ? 'bg-[#0E7044] text-white'
                            : 'bg-[#F3F4F6] text-[#4B5563] hover:bg-[#E5E7EB]'
                        }`}
                      >
                        {yr} Yrs
                      </button>
                    ))}
                  </div>
                </div>

                {/* Canadian Term vs Amortization Special Handling */}
                {countryCode === 'CA' && (
                  <div className="p-3 bg-[#E8F5E9] border border-[#C8E6C9] rounded-xl text-xs space-y-1.5">
                    <div className="font-bold text-[#0E7044] flex items-center gap-1.5">
                      <Info className="w-3.5 h-3.5" />
                      <span>Canadian Mortgage Term Distinction</span>
                    </div>
                    <p className="text-[11px] text-[#2E7D32]">
                      In Canada, your mortgage term (e.g. 5-year fixed) is renewed periodically, while the amortization
                      period ({amortizationYears} years) determines the regular payment size.
                    </p>
                  </div>
                )}

                {/* Payment Frequency */}
                <div className="space-y-1.5 pt-1">
                  <label className="text-xs font-bold text-[#374151]">Payment Frequency</label>
                  <select
                    value={paymentFrequency}
                    onChange={(e) => setPaymentFrequency(e.target.value as LoanPaymentFrequency)}
                    className="w-full px-3 py-2 border border-[#D1D5DB] rounded-xl text-xs font-bold text-[#1A1A1A] focus:ring-2 focus:ring-[#0E7044] focus:outline-none cursor-pointer"
                  >
                    <option value="monthly">Monthly (12 payments/year)</option>
                    <option value="biweekly">Bi-Weekly (26 regular payments/year)</option>
                    <option value="accelerated_biweekly">
                      Accelerated Bi-Weekly (26 half-monthly payments = 13 months paid)
                    </option>
                    <option value="weekly">Weekly (52 regular payments/year)</option>
                    <option value="accelerated_weekly">Accelerated Weekly (52 quarter-monthly payments)</option>
                    <option value="semimonthly">Semi-Monthly (24 payments/year)</option>
                  </select>
                </div>

                {/* Extra Payments Injections (Mode or Drawer) */}
                {(activeMode === 'extra-payments' || activeMode === 'mortgage-calculator') && (
                  <div className="border-t border-[#E5E7EB] pt-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-bold text-[#1A1A1A] flex items-center gap-1.5">
                        <TrendingDown className="w-3.5 h-3.5 text-[#0E7044]" />
                        <span>Extra Payments (Pay Off Faster)</span>
                      </label>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <label className="text-[11px] font-semibold text-[#6B7280]">Extra Monthly ($)</label>
                        <input
                          type="number"
                          value={extraPaymentMonthly}
                          onChange={(e) => setExtraPaymentMonthly(Number(e.target.value))}
                          placeholder="e.g. 200"
                          className="w-full px-3 py-1.5 border border-[#D1D5DB] rounded-lg text-xs font-bold text-[#1A1A1A] focus:ring-2 focus:ring-[#0E7044] focus:outline-none"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[11px] font-semibold text-[#6B7280]">Annual Lump Sum ($)</label>
                        <input
                          type="number"
                          value={extraPaymentAnnual}
                          onChange={(e) => setExtraPaymentAnnual(Number(e.target.value))}
                          placeholder="e.g. 2000"
                          className="w-full px-3 py-1.5 border border-[#D1D5DB] rounded-lg text-xs font-bold text-[#1A1A1A] focus:ring-2 focus:ring-[#0E7044] focus:outline-none"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Property Tax & Insurance Advanced Accordion */}
                {activeMode === 'mortgage-calculator' && (
                  <div className="border-t border-[#E5E7EB] pt-4 space-y-3">
                    <div className="text-xs font-bold text-[#374151]">Taxes, Insurance & HOA (PITI Escrow)</div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <label className="text-[11px] font-semibold text-[#6B7280]">Property Tax (Annual $)</label>
                        <input
                          type="number"
                          value={propertyTaxAnnual}
                          onChange={(e) => setPropertyTaxAnnual(Number(e.target.value))}
                          className="w-full px-3 py-1.5 border border-[#D1D5DB] rounded-lg text-xs font-bold text-[#1A1A1A] focus:ring-2 focus:ring-[#0E7044] focus:outline-none"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[11px] font-semibold text-[#6B7280]">Homeowners Insurance ($/yr)</label>
                        <input
                          type="number"
                          value={homeInsuranceAnnual}
                          onChange={(e) => setHomeInsuranceAnnual(Number(e.target.value))}
                          className="w-full px-3 py-1.5 border border-[#D1D5DB] rounded-lg text-xs font-bold text-[#1A1A1A] focus:ring-2 focus:ring-[#0E7044] focus:outline-none"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right Output & Breakdown Panel */}
            <div className="lg:col-span-7 space-y-6">
              {/* Main Hero Result Card */}
              <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 sm:p-8 shadow-xs space-y-6">
                <div className="flex flex-wrap items-center justify-between gap-4 border-b border-[#E5E7EB] pb-5">
                  <div>
                    <span className="text-xs font-bold uppercase tracking-wider text-[#0E7044]">
                      {paymentFrequency === 'monthly'
                        ? 'Estimated Monthly Payment'
                        : `Estimated ${paymentFrequency.replace('_', ' ').toUpperCase()} Payment`}
                    </span>
                    <div className="text-4xl sm:text-5xl font-black text-[#1A1A1A] tracking-tight pt-1">
                      {pack.defaultCurrency}{' '}
                      {calculationResult.periodicPayment.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </div>
                    <div className="text-xs text-[#6B7280] pt-1">
                      Pure Principal & Interest (P&I) • Nominal Rate: {interestRate}% ({compounding.toLowerCase()}{' '}
                      compounding)
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleSaveScenario}
                      className="px-3.5 py-2 bg-[#F3F4F6] hover:bg-[#E5E7EB] text-[#1A1A1A] rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer"
                    >
                      {isScenarioSaved ? <BookmarkCheck className="w-4 h-4 text-[#0E7044]" /> : <Bookmark className="w-4 h-4" />}
                      <span>{isScenarioSaved ? 'Saved!' : 'Save Scenario'}</span>
                    </button>
                    <button
                      onClick={() => setShowExplainability(!showExplainability)}
                      className="px-3.5 py-2 bg-[#E8F5E9] hover:bg-[#C8E6C9] text-[#0E7044] rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer"
                    >
                      <HelpCircle className="w-4 h-4" />
                      <span>Methodology</span>
                    </button>
                  </div>
                </div>

                {/* Total Housing Payment (PITI) Callout for Mortgages */}
                {activeMode === 'mortgage-calculator' && (
                  <div className="p-4 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-bold text-[#374151]">Total Estimated Housing Payment (PITI)</span>
                      <span className="text-lg font-extrabold text-[#1A1A1A]">
                        {pack.defaultCurrency}{' '}
                        {calculationResult.estimatedMonthlyHousingPayment.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                        <span className="text-xs font-normal text-[#6B7280]"> / month</span>
                      </span>
                    </div>

                    {/* Breakdown Bar */}
                    <div className="w-full bg-[#E5E7EB] h-3 rounded-full overflow-hidden flex">
                      <div
                        style={{
                          width: `${(calculationResult.principalAndInterestPayment / calculationResult.estimatedMonthlyHousingPayment) * 100}%`,
                        }}
                        className="bg-[#0E7044] h-full"
                        title="Principal & Interest"
                      />
                      <div
                        style={{
                          width: `${(calculationResult.propertyTaxMonthly / calculationResult.estimatedMonthlyHousingPayment) * 100}%`,
                        }}
                        className="bg-[#2563EB] h-full"
                        title="Property Tax"
                      />
                      <div
                        style={{
                          width: `${(calculationResult.homeInsuranceMonthly / calculationResult.estimatedMonthlyHousingPayment) * 100}%`,
                        }}
                        className="bg-[#D97706] h-full"
                        title="Home Insurance"
                      />
                      {calculationResult.mortgageInsuranceMonthly > 0 && (
                        <div
                          style={{
                            width: `${(calculationResult.mortgageInsuranceMonthly / calculationResult.estimatedMonthlyHousingPayment) * 100}%`,
                          }}
                          className="bg-[#9333EA] h-full"
                          title="Mortgage Insurance (PMI)"
                        />
                      )}
                    </div>

                    {/* Component Labels */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-1 text-xs">
                      <div className="flex items-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-full bg-[#0E7044]" />
                        <span className="text-[#4B5563]">P&I:</span>
                        <span className="font-bold text-[#1A1A1A]">
                          ${calculationResult.principalAndInterestPayment.toFixed(0)}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-full bg-[#2563EB]" />
                        <span className="text-[#4B5563]">Tax:</span>
                        <span className="font-bold text-[#1A1A1A]">
                          ${calculationResult.propertyTaxMonthly.toFixed(0)}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-full bg-[#D97706]" />
                        <span className="text-[#4B5563]">Ins:</span>
                        <span className="font-bold text-[#1A1A1A]">
                          ${calculationResult.homeInsuranceMonthly.toFixed(0)}
                        </span>
                      </div>
                      {calculationResult.mortgageInsuranceMonthly > 0 && (
                        <div className="flex items-center gap-1.5">
                          <span className="w-2.5 h-2.5 rounded-full bg-[#9333EA]" />
                          <span className="text-[#4B5563]">PMI:</span>
                          <span className="font-bold text-[#1A1A1A]">
                            ${calculationResult.mortgageInsuranceMonthly.toFixed(0)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* 4 Core Summary Metrics */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="p-3 bg-[#FBFBFC] rounded-xl border border-[#E5E7EB] space-y-1">
                    <span className="text-[10px] font-semibold text-[#6B7280] uppercase">Total Principal</span>
                    <div className="text-sm font-extrabold text-[#1A1A1A]">
                      {pack.defaultCurrency} {calculationResult.totalPrincipalPaid.toLocaleString()}
                    </div>
                    <div className="text-[10px] text-[#0E7044] font-semibold">
                      LTV: {calculationResult.loanToValueRatio}%
                    </div>
                  </div>

                  <div className="p-3 bg-[#FBFBFC] rounded-xl border border-[#E5E7EB] space-y-1">
                    <span className="text-[10px] font-semibold text-[#6B7280] uppercase">Total Interest</span>
                    <div className="text-sm font-extrabold text-[#1A1A1A]">
                      {pack.defaultCurrency} {calculationResult.totalInterestPaid.toLocaleString()}
                    </div>
                    <div className="text-[10px] text-[#6B7280]">Over {amortizationYears} years</div>
                  </div>

                  <div className="p-3 bg-[#FBFBFC] rounded-xl border border-[#E5E7EB] space-y-1">
                    <span className="text-[10px] font-semibold text-[#6B7280] uppercase">Total Repayment</span>
                    <div className="text-sm font-extrabold text-[#1A1A1A]">
                      {pack.defaultCurrency} {calculationResult.totalRepayment.toLocaleString()}
                    </div>
                    <div className="text-[10px] text-[#6B7280]">Principal + Interest</div>
                  </div>

                  <div className="p-3 bg-[#FBFBFC] rounded-xl border border-[#E5E7EB] space-y-1">
                    <span className="text-[10px] font-semibold text-[#6B7280] uppercase">
                      {pack.aprMetricType} Cost
                    </span>
                    <div className="text-sm font-extrabold text-[#0E7044]">
                      {calculationResult.calculatedAprOrAprc.toFixed(3)}%
                    </div>
                    <div className="text-[10px] text-[#6B7280]">{pack.aprMetricName.split(' ')[0]}</div>
                  </div>
                </div>

                {/* Extra Payment Savings Callout */}
                {calculationResult.interestSaved > 0 && (
                  <div className="p-4 bg-[#E8F5E9] border border-[#C8E6C9] rounded-xl flex items-center justify-between gap-4">
                    <div className="space-y-0.5">
                      <div className="text-xs font-bold text-[#0E7044] flex items-center gap-1.5">
                        <Sparkles className="w-4 h-4" />
                        <span>Payoff Acceleration Impact</span>
                      </div>
                      <p className="text-xs text-[#2E7D32]">
                        You will save{' '}
                        <strong className="font-extrabold text-[#0E7044]">
                          {pack.defaultCurrency} {calculationResult.interestSaved.toLocaleString()}
                        </strong>{' '}
                        in interest and be mortgage-free{' '}
                        <strong className="font-extrabold text-[#0E7044]">
                          {Math.floor(calculationResult.monthsSaved / 12)} yrs {calculationResult.monthsSaved % 12} mos
                        </strong>{' '}
                        sooner (Payoff: {calculationResult.payoffDateActual}).
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Explainability / Methodology Drawer */}
              {showExplainability && (
                <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 shadow-xs space-y-4 animate-in fade-in duration-200">
                  <div className="flex items-center justify-between border-b border-[#E5E7EB] pb-3">
                    <h3 className="font-bold text-sm text-[#1A1A1A] flex items-center gap-2">
                      <HelpCircle className="w-4 h-4 text-[#0E7044]" />
                      <span>Calculation Trace & Regulatory Ruleset</span>
                    </h3>
                    <span className="text-xs text-[#6B7280]">{pack.countryName} Standard</span>
                  </div>

                  <div className="space-y-3 text-xs">
                    {calculationResult.explainabilityTrace.map((step) => (
                      <div key={step.stepNumber} className="p-3 bg-[#F9FAFB] rounded-xl border border-[#E5E7EB] space-y-1">
                        <div className="font-bold text-[#0E7044]">
                          Step {step.stepNumber}: {step.category}
                        </div>
                        <p className="text-[#4B5563] leading-relaxed">{step.description}</p>
                      </div>
                    ))}
                  </div>

                  <div className="p-3 bg-[#FFFBEB] border border-[#FDE68A] rounded-xl text-xs space-y-1">
                    <div className="font-bold text-[#92400E] flex items-center gap-1.5">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      <span>Regulatory Disclaimer</span>
                    </div>
                    <p className="text-[#B45309] text-[11px] leading-relaxed">{calculationResult.disclaimer}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* 2. AMORTIZATION SCHEDULE TABLE VIEW */}
        {/* ========================================================================= */}
        {activeMode === 'amortization' && (
          <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 shadow-xs space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-4 border-b border-[#E5E7EB] pb-4">
              <div>
                <h2 className="text-lg font-bold text-[#1A1A1A]">Full Amortization Schedule</h2>
                <p className="text-xs text-[#6B7280]">
                  Detailed breakdown of principal, interest, extra payments, and remaining balances over the full loan
                  term.
                </p>
              </div>

              <div className="flex items-center gap-2">
                <div className="flex bg-[#F3F4F6] p-1 rounded-xl">
                  <button
                    onClick={() => setAmortizationViewType('yearly')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                      amortizationViewType === 'yearly' ? 'bg-white text-[#1A1A1A] shadow-xs' : 'text-[#6B7280]'
                    }`}
                  >
                    Annual Rollup
                  </button>
                  <button
                    onClick={() => setAmortizationViewType('monthly')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                      amortizationViewType === 'monthly' ? 'bg-white text-[#1A1A1A] shadow-xs' : 'text-[#6B7280]'
                    }`}
                  >
                    Payment-by-Payment
                  </button>
                </div>
              </div>
            </div>

            {/* Table Container */}
            <div className="overflow-x-auto">
              {amortizationViewType === 'yearly' ? (
                <table className="w-full text-left text-xs">
                  <thead className="bg-[#F9FAFB] text-[#6B7280] font-semibold border-b border-[#E5E7EB]">
                    <tr>
                      <th className="py-3 px-4">Year</th>
                      <th className="py-3 px-4">Opening Balance</th>
                      <th className="py-3 px-4">Principal Paid</th>
                      <th className="py-3 px-4">Interest Paid</th>
                      <th className="py-3 px-4">Extra Principal</th>
                      <th className="py-3 px-4">Total Paid</th>
                      <th className="py-3 px-4">Ending Balance</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#F3F4F6]">
                    {calculationResult.yearlySummaries.map((row) => (
                      <tr key={row.yearNumber} className="hover:bg-[#FBFBFC]">
                        <td className="py-2.5 px-4 font-bold text-[#1A1A1A]">Year {row.yearNumber}</td>
                        <td className="py-2.5 px-4 font-mono">${row.openingBalance.toLocaleString()}</td>
                        <td className="py-2.5 px-4 font-mono font-bold text-[#0E7044]">
                          ${row.principalPaid.toLocaleString()}
                        </td>
                        <td className="py-2.5 px-4 font-mono text-[#6B7280]">${row.interestPaid.toLocaleString()}</td>
                        <td className="py-2.5 px-4 font-mono text-[#2563EB]">
                          ${row.extraPrincipalPaid.toLocaleString()}
                        </td>
                        <td className="py-2.5 px-4 font-mono font-bold text-[#1A1A1A]">
                          ${row.totalPaid.toLocaleString()}
                        </td>
                        <td className="py-2.5 px-4 font-mono font-bold text-[#1A1A1A]">
                          ${row.closingBalance.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="space-y-4">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-[#F9FAFB] text-[#6B7280] font-semibold border-b border-[#E5E7EB]">
                      <tr>
                        <th className="py-3 px-4">#</th>
                        <th className="py-3 px-4">Date</th>
                        <th className="py-3 px-4">Opening Balance</th>
                        <th className="py-3 px-4">Scheduled Payment</th>
                        <th className="py-3 px-4">Principal</th>
                        <th className="py-3 px-4">Interest</th>
                        <th className="py-3 px-4">Extra</th>
                        <th className="py-3 px-4">Ending Balance</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#F3F4F6]">
                      {calculationResult.amortizationSchedule
                        .slice((schedulePage - 1) * 36, schedulePage * 36)
                        .map((row) => (
                          <tr key={row.paymentNumber} className="hover:bg-[#FBFBFC]">
                            <td className="py-2 px-4 font-bold text-[#6B7280]">{row.paymentNumber}</td>
                            <td className="py-2 px-4 text-[#4B5563]">{row.paymentDate}</td>
                            <td className="py-2 px-4 font-mono">${row.openingBalance.toLocaleString()}</td>
                            <td className="py-2 px-4 font-mono font-bold">${row.scheduledPayment.toLocaleString()}</td>
                            <td className="py-2 px-4 font-mono font-bold text-[#0E7044]">
                              ${row.principalPortion.toLocaleString()}
                            </td>
                            <td className="py-2 px-4 font-mono text-[#6B7280]">
                              ${row.interestPortion.toLocaleString()}
                            </td>
                            <td className="py-2 px-4 font-mono text-[#2563EB]">
                              ${row.extraPrincipal.toLocaleString()}
                            </td>
                            <td className="py-2 px-4 font-mono font-bold">${row.closingBalance.toLocaleString()}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>

                  {/* Pagination */}
                  <div className="flex justify-between items-center text-xs text-[#6B7280] pt-2">
                    <span>
                      Showing payments {(schedulePage - 1) * 36 + 1} to{' '}
                      {Math.min(schedulePage * 36, calculationResult.amortizationSchedule.length)} of{' '}
                      {calculationResult.amortizationSchedule.length}
                    </span>
                    <div className="flex gap-1">
                      <button
                        disabled={schedulePage === 1}
                        onClick={() => setSchedulePage(schedulePage - 1)}
                        className="px-3 py-1 bg-[#F3F4F6] rounded-lg disabled:opacity-40 cursor-pointer"
                      >
                        Prev
                      </button>
                      <button
                        disabled={
                          schedulePage * 36 >= calculationResult.amortizationSchedule.length
                        }
                        onClick={() => setSchedulePage(schedulePage + 1)}
                        className="px-3 py-1 bg-[#F3F4F6] rounded-lg disabled:opacity-40 cursor-pointer"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* 3. HOME AFFORDABILITY ESTIMATOR */}
        {/* ========================================================================= */}
        {activeMode === 'affordability' && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-5 bg-white border border-[#E5E7EB] rounded-2xl p-6 shadow-xs space-y-5">
              <div className="flex items-center gap-2 border-b border-[#E5E7EB] pb-3">
                <Sliders className="w-4 h-4 text-[#0E7044]" />
                <h2 className="font-bold text-sm text-[#1A1A1A]">Household Income & Debt Profile</h2>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-[#374151]">Gross Annual Household Income</label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-xs text-[#9CA3AF] font-bold">$</span>
                  <input
                    type="number"
                    value={grossAnnualIncome}
                    onChange={(e) => setGrossAnnualIncome(Number(e.target.value))}
                    className="w-full pl-8 pr-3 py-2 border border-[#D1D5DB] rounded-xl text-sm font-bold text-[#1A1A1A] focus:ring-2 focus:ring-[#0E7044] focus:outline-none"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-[#374151]">Monthly Recurring Debt Payments</label>
                <p className="text-[11px] text-[#6B7280]">Auto loans, student loans, credit card minimums</p>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-xs text-[#9CA3AF] font-bold">$</span>
                  <input
                    type="number"
                    value={monthlyDebtPayments}
                    onChange={(e) => setMonthlyDebtPayments(Number(e.target.value))}
                    className="w-full pl-8 pr-3 py-2 border border-[#D1D5DB] rounded-xl text-sm font-bold text-[#1A1A1A] focus:ring-2 focus:ring-[#0E7044] focus:outline-none"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-[#374151]">Available Down Payment Funds</label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-xs text-[#9CA3AF] font-bold">$</span>
                  <input
                    type="number"
                    value={downPaymentSaved}
                    onChange={(e) => setDownPaymentSaved(Number(e.target.value))}
                    className="w-full pl-8 pr-3 py-2 border border-[#D1D5DB] rounded-xl text-sm font-bold text-[#1A1A1A] focus:ring-2 focus:ring-[#0E7044] focus:outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 pt-2">
                <div className="space-y-1">
                  <label className="text-xs font-bold text-[#374151]">Loan Term</label>
                  <select
                    value={amortizationYears}
                    onChange={(e) => setAmortizationYears(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-[#D1D5DB] rounded-xl text-xs font-bold text-[#1A1A1A] focus:ring-2 focus:ring-[#0E7044] focus:outline-none"
                  >
                    <option value={30}>30 Years</option>
                    <option value={25}>25 Years</option>
                    <option value={20}>20 Years</option>
                    <option value={15}>15 Years</option>
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-[#374151]">Expected Rate (%)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={interestRate}
                    onChange={(e) => setInterestRate(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-[#D1D5DB] rounded-xl text-xs font-bold text-[#1A1A1A] focus:ring-2 focus:ring-[#0E7044] focus:outline-none"
                  />
                </div>
              </div>
            </div>

            {/* Affordability Output Cards */}
            <div className="lg:col-span-7 space-y-6">
              <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 sm:p-8 shadow-xs space-y-6">
                <div>
                  <span className="text-xs font-bold uppercase tracking-wider text-[#0E7044]">
                    Estimated Affordable Home Price Range
                  </span>
                  <div className="text-4xl sm:text-5xl font-black text-[#1A1A1A] tracking-tight pt-1">
                    ${affordabilityResult.maxAffordableHomePrice.toLocaleString()}
                  </div>
                  <div className="text-xs text-[#6B7280] pt-1">
                    Conservative: ${affordabilityResult.priceRangeConservative.toLocaleString()} • Aggressive: $
                    {affordabilityResult.priceRangeAggressive.toLocaleString()}
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div className="p-3 bg-[#FBFBFC] rounded-xl border border-[#E5E7EB] space-y-1">
                    <span className="text-[10px] font-semibold text-[#6B7280] uppercase">Max Monthly Budget</span>
                    <div className="text-sm font-extrabold text-[#1A1A1A]">
                      ${affordabilityResult.monthlyBudgetPITI.toLocaleString()} / mo
                    </div>
                    <div className="text-[10px] text-[#6B7280]">Total PITI payment</div>
                  </div>

                  <div className="p-3 bg-[#FBFBFC] rounded-xl border border-[#E5E7EB] space-y-1">
                    <span className="text-[10px] font-semibold text-[#6B7280] uppercase">Max Loan Principal</span>
                    <div className="text-sm font-extrabold text-[#0E7044]">
                      ${affordabilityResult.maxAffordableLoanAmount.toLocaleString()}
                    </div>
                    <div className="text-[10px] text-[#6B7280]">
                      + ${affordabilityResult.suggestedDownPayment.toLocaleString()} down
                    </div>
                  </div>

                  <div className="p-3 bg-[#FBFBFC] rounded-xl border border-[#E5E7EB] space-y-1">
                    <span className="text-[10px] font-semibold text-[#6B7280] uppercase">Debt Ratios (DTI)</span>
                    <div className="text-sm font-extrabold text-[#1A1A1A]">
                      {affordabilityResult.frontEndDtiActual}% / {affordabilityResult.backEndDtiActual}%
                    </div>
                    <div className="text-[10px] text-[#6B7280]">Front / Back DTI</div>
                  </div>
                </div>

                <div className="p-4 bg-[#F9FAFB] rounded-xl border border-[#E5E7EB] text-xs space-y-2">
                  <div className="font-bold text-[#1A1A1A]">Model Assumptions & Guidelines</div>
                  <ul className="list-disc pl-4 space-y-1 text-[#4B5563]">
                    {affordabilityResult.assumptions.map((assump, i) => (
                      <li key={i}>{assump}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* 4. REFINANCE & BREAK-EVEN ANALYZER */}
        {/* ========================================================================= */}
        {activeMode === 'refinance' && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-5 bg-white border border-[#E5E7EB] rounded-2xl p-6 shadow-xs space-y-5">
              <div className="flex items-center gap-2 border-b border-[#E5E7EB] pb-3">
                <RefreshCw className="w-4 h-4 text-[#0E7044]" />
                <h2 className="font-bold text-sm text-[#1A1A1A]">Current vs New Mortgage Terms</h2>
              </div>

              <div className="space-y-3">
                <div className="text-xs font-bold text-[#1A1A1A]">1. Current Mortgage</div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label className="text-[11px] font-semibold text-[#6B7280]">Current Balance ($)</label>
                    <input
                      type="number"
                      value={refinanceCurrentBalance}
                      onChange={(e) => setRefinanceCurrentBalance(Number(e.target.value))}
                      className="w-full px-3 py-1.5 border border-[#D1D5DB] rounded-lg text-xs font-bold text-[#1A1A1A]"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[11px] font-semibold text-[#6B7280]">Current Rate (%)</label>
                    <input
                      type="number"
                      step="0.05"
                      value={refinanceCurrentRate}
                      onChange={(e) => setRefinanceCurrentRate(Number(e.target.value))}
                      className="w-full px-3 py-1.5 border border-[#D1D5DB] rounded-lg text-xs font-bold text-[#1A1A1A]"
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <label className="text-[11px] font-semibold text-[#6B7280]">Remaining Years on Current Loan</label>
                  <input
                    type="number"
                    value={refinanceCurrentRemainingYears}
                    onChange={(e) => setRefinanceCurrentRemainingYears(Number(e.target.value))}
                    className="w-full px-3 py-1.5 border border-[#D1D5DB] rounded-lg text-xs font-bold text-[#1A1A1A]"
                  />
                </div>
              </div>

              <div className="space-y-3 border-t border-[#E5E7EB] pt-3">
                <div className="text-xs font-bold text-[#1A1A1A]">2. New Refinanced Mortgage</div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label className="text-[11px] font-semibold text-[#6B7280]">New Rate (%)</label>
                    <input
                      type="number"
                      step="0.05"
                      value={refinanceNewRate}
                      onChange={(e) => setRefinanceNewRate(Number(e.target.value))}
                      className="w-full px-3 py-1.5 border border-[#D1D5DB] rounded-lg text-xs font-bold text-[#0E7044]"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[11px] font-semibold text-[#6B7280]">New Term (Years)</label>
                    <select
                      value={refinanceNewTermYears}
                      onChange={(e) => setRefinanceNewTermYears(Number(e.target.value))}
                      className="w-full px-3 py-1.5 border border-[#D1D5DB] rounded-lg text-xs font-bold text-[#1A1A1A]"
                    >
                      <option value={30}>30 Years</option>
                      <option value={20}>20 Years</option>
                      <option value={15}>15 Years</option>
                    </select>
                  </div>
                </div>
                <div className="space-y-1">
                  <label className="text-[11px] font-semibold text-[#6B7280]">Refinance Closing Costs ($)</label>
                  <input
                    type="number"
                    value={refinanceClosingCostsInput}
                    onChange={(e) => setRefinanceClosingCostsInput(Number(e.target.value))}
                    className="w-full px-3 py-1.5 border border-[#D1D5DB] rounded-lg text-xs font-bold text-[#1A1A1A]"
                  />
                </div>
              </div>
            </div>

            {/* Refinance Result Cards */}
            <div className="lg:col-span-7 space-y-6">
              <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 sm:p-8 shadow-xs space-y-6">
                <div>
                  <span className="text-xs font-bold uppercase tracking-wider text-[#0E7044]">
                    Estimated Monthly Cashflow Savings
                  </span>
                  <div className="text-4xl sm:text-5xl font-black text-[#0E7044] tracking-tight pt-1">
                    ${refinanceResult.monthlySavings.toFixed(2)}
                    <span className="text-xs font-normal text-[#6B7280]"> / month</span>
                  </div>
                  <p className="text-xs text-[#6B7280] pt-1">
                    Current P&I: ${refinanceResult.currentMonthlyPayment.toFixed(2)} → New P&I: $
                    {refinanceResult.newMonthlyPayment.toFixed(2)}
                  </p>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div className="p-3 bg-[#FBFBFC] rounded-xl border border-[#E5E7EB] space-y-1">
                    <span className="text-[10px] font-semibold text-[#6B7280] uppercase">Break-Even Period</span>
                    <div className="text-base font-extrabold text-[#1A1A1A]">
                      {refinanceResult.breakEvenMonths} Months
                    </div>
                    <div className="text-[10px] text-[#6B7280]">
                      To recover ${refinanceClosingCostsInput.toLocaleString()} costs
                    </div>
                  </div>

                  <div className="p-3 bg-[#FBFBFC] rounded-xl border border-[#E5E7EB] space-y-1">
                    <span className="text-[10px] font-semibold text-[#6B7280] uppercase">Lifetime Interest Saved</span>
                    <div className="text-base font-extrabold text-[#0E7044]">
                      ${refinanceResult.lifetimeInterestSavings.toLocaleString()}
                    </div>
                    <div className="text-[10px] text-[#6B7280]">Gross interest reduction</div>
                  </div>

                  <div className="p-3 bg-[#FBFBFC] rounded-xl border border-[#E5E7EB] space-y-1">
                    <span className="text-[10px] font-semibold text-[#6B7280] uppercase">Net Lifetime Benefit</span>
                    <div className="text-base font-extrabold text-[#1A1A1A]">
                      ${refinanceResult.netLifetimeSavings.toLocaleString()}
                    </div>
                    <div className="text-[10px] text-[#6B7280]">After closing costs</div>
                  </div>
                </div>

                <div className="p-4 bg-[#E8F5E9] border border-[#C8E6C9] rounded-xl text-xs text-[#2E7D32] leading-relaxed">
                  <strong className="font-bold text-[#0E7044]">Analysis: </strong>
                  {refinanceResult.recommendationSummary}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* 5. SCENARIO COMPARISON (SIDE-BY-SIDE) */}
        {/* ========================================================================= */}
        {activeMode === 'scenario-comparison' && (
          <div className="space-y-6">
            <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 shadow-xs">
              <h2 className="text-lg font-bold text-[#1A1A1A] pb-1">Side-by-Side Mortgage Scenarios</h2>
              <p className="text-xs text-[#6B7280] pb-6">
                Compare terms, rates, monthly payments, and total interest across 3 distinct borrowing strategies.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Scenario A: 30-Year Fixed */}
                <div className="p-5 bg-[#FBFBFC] border-2 border-[#0E7044] rounded-2xl space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-[#E8F5E9] text-[#0E7044]">
                      Scenario A (Baseline)
                    </span>
                    <span className="text-xs font-bold text-[#1A1A1A]">30-Yr Fixed</span>
                  </div>
                  <div>
                    <div className="text-3xl font-black text-[#1A1A1A]">$2,275.44</div>
                    <span className="text-xs text-[#6B7280]">per month (P&I)</span>
                  </div>
                  <div className="space-y-2 text-xs border-t border-[#E5E7EB] pt-3">
                    <div className="flex justify-between">
                      <span className="text-[#6B7280]">Loan Principal:</span>
                      <span className="font-bold">$360,000</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#6B7280]">Interest Rate:</span>
                      <span className="font-bold">6.50%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#6B7280]">Total Interest:</span>
                      <span className="font-bold text-[#D97706]">$459,158</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#6B7280]">Total Repayment:</span>
                      <span className="font-bold">$819,158</span>
                    </div>
                  </div>
                </div>

                {/* Scenario B: 15-Year Fixed */}
                <div className="p-5 bg-white border border-[#E5E7EB] rounded-2xl space-y-4 shadow-2xs">
                  <div className="flex justify-between items-center">
                    <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-[#EFF6FF] text-[#2563EB]">
                      Scenario B (Accelerated)
                    </span>
                    <span className="text-xs font-bold text-[#1A1A1A]">15-Yr Fixed</span>
                  </div>
                  <div>
                    <div className="text-3xl font-black text-[#1A1A1A]">$2,989.87</div>
                    <span className="text-xs text-[#6B7280]">+$714/mo higher payment</span>
                  </div>
                  <div className="space-y-2 text-xs border-t border-[#E5E7EB] pt-3">
                    <div className="flex justify-between">
                      <span className="text-[#6B7280]">Loan Principal:</span>
                      <span className="font-bold">$360,000</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#6B7280]">Interest Rate:</span>
                      <span className="font-bold text-[#0E7044]">5.75% (-0.75%)</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#6B7280]">Total Interest:</span>
                      <span className="font-bold text-[#0E7044]">$178,177 (-$280,981)</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#6B7280]">Total Repayment:</span>
                      <span className="font-bold">$538,177</span>
                    </div>
                  </div>
                </div>

                {/* Scenario C: 30-Year Fixed + $300/mo Extra */}
                <div className="p-5 bg-white border border-[#E5E7EB] rounded-2xl space-y-4 shadow-2xs">
                  <div className="flex justify-between items-center">
                    <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-[#FAF5FF] text-[#9333EA]">
                      Scenario C (Prepayment)
                    </span>
                    <span className="text-xs font-bold text-[#1A1A1A]">30-Yr + $300/mo</span>
                  </div>
                  <div>
                    <div className="text-3xl font-black text-[#1A1A1A]">$2,575.44</div>
                    <span className="text-xs text-[#6B7280]">Paid off in 21.8 yrs</span>
                  </div>
                  <div className="space-y-2 text-xs border-t border-[#E5E7EB] pt-3">
                    <div className="flex justify-between">
                      <span className="text-[#6B7280]">Loan Principal:</span>
                      <span className="font-bold">$360,000</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#6B7280]">Interest Rate:</span>
                      <span className="font-bold">6.50%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#6B7280]">Total Interest:</span>
                      <span className="font-bold text-[#0E7044]">$314,640 (-$144,518)</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#6B7280]">Payoff Saved:</span>
                      <span className="font-bold text-[#0E7044]">8.2 Years Sooner</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* 6. LOAN COMMAND CENTER & QA TELEMETRY */}
        {/* ========================================================================= */}
        {activeMode === 'command-center' && (
          <div className="space-y-6">
            {/* Top Telemetry KPI Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="bg-white p-5 rounded-2xl border border-[#E5E7EB] shadow-xs space-y-1">
                <span className="text-xs font-semibold text-[#6B7280]">Active Market Packs</span>
                <div className="text-2xl font-black text-[#1A1A1A]">{LOAN_TELEMETRY_DATA.activeMarketsCount}</div>
                <span className="text-[11px] text-[#0E7044] font-semibold">US, CA, GB, FR, DE, AU, IN, SG</span>
              </div>

              <div className="bg-white p-5 rounded-2xl border border-[#E5E7EB] shadow-xs space-y-1">
                <span className="text-xs font-semibold text-[#6B7280]">Golden Test Pass Rate</span>
                <div className="text-2xl font-black text-[#0E7044]">{LOAN_TELEMETRY_DATA.goldenTestPassRate}%</div>
                <span className="text-[11px] text-[#0E7044] font-semibold">100% Deterministic Amortization</span>
              </div>

              <div className="bg-white p-5 rounded-2xl border border-[#E5E7EB] shadow-xs space-y-1">
                <span className="text-xs font-semibold text-[#6B7280]">Verified Formulas</span>
                <div className="text-2xl font-black text-[#1A1A1A]">{LOAN_TELEMETRY_DATA.verifiedFormulasCount}</div>
                <span className="text-[11px] text-[#6B7280]">Zero Float Accumulation</span>
              </div>

              <div className="bg-white p-5 rounded-2xl border border-[#E5E7EB] shadow-xs space-y-1">
                <span className="text-xs font-semibold text-[#6B7280]">Amortization Latency</span>
                <div className="text-2xl font-black text-[#1A1A1A]">
                  {LOAN_TELEMETRY_DATA.avgAmortizationLatencyMs} ms
                </div>
                <span className="text-[11px] text-[#0E7044] font-semibold">Sub-millisecond 360-month engine</span>
              </div>
            </div>

            {/* Golden Test Runner Suite */}
            <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 shadow-xs space-y-4">
              <div className="flex items-center justify-between border-b border-[#E5E7EB] pb-4">
                <div>
                  <h3 className="font-bold text-sm text-[#1A1A1A] flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#0E7044]" />
                    <span>Automated Golden Lending Test Suite (Regression Fixtures)</span>
                  </h3>
                  <p className="text-xs text-[#6B7280]">
                    Official benchmark verification across US CFPB, Canadian FCAC semi-annual, French Barème, and UK
                    FCA MCOB.
                  </p>
                </div>

                <button
                  onClick={handleRunTestSuite}
                  className="px-4 py-2 bg-[#0E7044] hover:bg-[#0B5936] text-white rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer shadow-xs"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  <span>Execute Test Suite</span>
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-[#F9FAFB] text-[#6B7280] font-semibold border-b border-[#E5E7EB]">
                    <tr>
                      <th className="py-2.5 px-3">Test Case ID</th>
                      <th className="py-2.5 px-3">Scenario & Country</th>
                      <th className="py-2.5 px-3">Expected Payment</th>
                      <th className="py-2.5 px-3">Actual Payment</th>
                      <th className="py-2.5 px-3">Diff</th>
                      <th className="py-2.5 px-3">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#F3F4F6]">
                    {(testSuiteRunResult ? testSuiteRunResult.results : runGoldenLoanTests().results).map((tc) => (
                      <tr key={tc.id} className="hover:bg-[#FBFBFC]">
                        <td className="py-2.5 px-3 font-mono font-bold text-[#0E7044]">{tc.id}</td>
                        <td className="py-2.5 px-3 font-semibold text-[#1A1A1A]">{tc.scenarioName}</td>
                        <td className="py-2.5 px-3 font-mono">${tc.expectedPayment.toFixed(2)}</td>
                        <td className="py-2.5 px-3 font-mono font-bold">${tc.actualPayment.toFixed(2)}</td>
                        <td className="py-2.5 px-3 font-mono text-[#6B7280]">${tc.diffPayment.toFixed(2)}</td>
                        <td className="py-2.5 px-3">
                          <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-[#E8F5E9] text-[#0E7044] border border-[#C8E6C9]">
                            {tc.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Recent Lending Incidents / Audits */}
            <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 shadow-xs space-y-4">
              <h3 className="font-bold text-sm text-[#1A1A1A] flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-[#0E7044]" />
                <span>Lending Engine Governance & Audit Log</span>
              </h3>

              <div className="space-y-3">
                {RECENT_LENDING_INCIDENTS.map((inc) => (
                  <div key={inc.id} className="p-4 bg-[#FBFBFC] rounded-xl border border-[#E5E7EB] space-y-1.5 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-[10px] font-bold text-[#0E7044] bg-[#E8F5E9] px-2 py-0.5 rounded">
                        {inc.id}
                      </span>
                      <span className="text-[10px] text-[#6B7280]">{inc.reportedAt.slice(0, 10)}</span>
                    </div>
                    <div className="font-bold text-[#1A1A1A]">{inc.title}</div>
                    <p className="text-[#4B5563] leading-relaxed">{inc.description}</p>
                    {inc.resolutionNotes && (
                      <div className="text-[11px] text-[#0E7044] font-semibold pt-1">
                        Resolution: {inc.resolutionNotes}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
